import { IOffer, ITableDemandItem, TotalWinnerFactorType } from '@models/winnerSelection/ITableDemand';

/**
 * Лидер по конкретному критерию — оффер с максимальным rating среди всех.
 *
 * Не выделяем лидера, если:
 * - максимум невалидный (0 / null / -Infinity);
 * - у текущего оффера нет рейтинга по этому критерию;
 * - все офферы имеют одинаковый рейтинг (тогда "лидер" теряет смысл).
 *
 * Сравнение через toFixed(4) — защищает от floating-point дребезга при равных оценках.
 */
export const checkIsRatingWinner = (crit: ITableDemandItem, offerKey: string): boolean => {
    const ratings = Object.values(crit.offers).map((c) => c.rating ?? -Infinity);
    const max = Math.max(...ratings);
    if (max === -Infinity || max === 0 || max === null) return false;
    const current = crit.offers[offerKey]?.rating;
    if (current == null) return false;
    const winners = Object.values(crit.offers).filter((c) => c.rating?.toFixed(4) === current.toFixed(4));
    if (winners.length === Object.keys(crit.offers).length) return false;
    return current.toFixed(4) === max.toFixed(4);
};

/**
 * Победитель по цене — минимальная цена сравнения (comparePrice, в рублях) среди
 * НЕотозванных предложений. Сравнение ведётся по рублёвой цене (бэк отдаёт её в
 * comparePrice из поля «Цена_предложения») независимо от валюты предложения,
 * поэтому в нём участвуют ВСЕ предложения — в т.ч. в иной валюте, — а не только
 * в базовой валюте закупки.
 */
export const checkIsPriceWinner = (offers: IOffer[] | undefined, currentKey: string): boolean => {
    if (!offers || offers.length === 0) return false;
    const activeOffers = offers.filter((o) => !o.isWithdrawn && o.comparePrice != null);
    if (activeOffers.length === 0) return false;
    const min = Math.min(...activeOffers.map((item) => item.comparePrice as number)).toFixed(2);
    const winners = activeOffers.filter((item) => item.comparePrice?.toFixed(2) === min);
    return winners.some((item) => item.key === currentKey);
};

/**
 * Победитель по итоговой оценке (summaryScore) — у кого максимум.
 */
export const checkIsSummaryScoreWinner = (offers: IOffer[] | undefined, currentKey: string): boolean => {
    if (!offers || offers.length === 0) return false;
    if (offers.every((item) => item.summaryScore == null)) return false;
    const max = Math.max(...offers.map((item) => (item.summaryScore !== null ? item.summaryScore : -Infinity))).toFixed(
        4,
    );
    const winners = offers.filter((item) => item.summaryScore?.toFixed(4) === max);
    return winners.some((item) => item.key === currentKey);
};

/**
 * Победитель по рангу — у кого минимальный rank (1 = первое место).
 */
export const checkIsRankWinner = (offers: IOffer[] | undefined, currentKey: string): boolean => {
    if (!offers || offers.length === 0) return false;
    if (offers.every((o) => o.rank == null || o.rank === '')) return false;
    const min = Math.min(...offers.map((o) => (o.rank != null && o.rank !== '' ? Number(o.rank) : Infinity)));
    const winners = offers.filter((o) => o.rank != null && o.rank !== '' && Number(o.rank) === min);
    return winners.some((o) => o.key === currentKey);
};

/**
 * Главный winner-flag для подсветки колонки целиком. Берёт фактор из настроек:
 * - 'rank' → лидер по рангу;
 * - 'summaryScore' (default) → лидер по итоговой оценке.
 */
export const checkIsTotalWinner = (
    offers: IOffer[] | undefined,
    currentKey: string,
    factor: TotalWinnerFactorType | undefined,
): boolean => {
    switch (factor) {
        case 'rank':
            return checkIsRankWinner(offers, currentKey);
        case 'summaryScore':
        default:
            return checkIsSummaryScoreWinner(offers, currentKey);
    }
};
